import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BeakerIcon, PlusIcon, EyeIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { chemicalInventoryApi } from "../../../api/chemical-inventory";
import { getChemicalInventorySettings } from "../../../api/hse/chemicalInventorySettings";
import { toast } from "react-hot-toast";
import RoleOnly from "../../../routes/roleOnly";
import {
  CHEMICAL_INVENTORY_WRITE_ROLES,
  normalizeChemicalInventorySettings,
} from "../../../utils/chemicalInventoryAccess";

export const ProductChemicalInventory = ({ product }) => {
  const { t } = useTranslation();
  const [chemicalInventory, setChemicalInventory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoCreateEnabled, setAutoCreateEnabled] = useState(false);

  useEffect(() => {
    if (product?.id) {
      fetchChemicalInventory();
    }
  }, [product?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await getChemicalInventorySettings();
        if (cancelled) return;
        const settings = normalizeChemicalInventorySettings(raw);
        setAutoCreateEnabled(Boolean(settings.auto_create_from_products));
      } catch {
        if (!cancelled) setAutoCreateEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchChemicalInventory = async () => {
    try {
      setLoading(true);
      const response = await chemicalInventoryApi.getChemicalInventories({
        product_id: product.id,
        per_page: 1,
      });

      if (response.data && response.data.length > 0) {
        setChemicalInventory(response.data[0]);
      }
    } catch (err) {
      console.error("Error fetching chemical inventory:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToChemicalInventory = async () => {
    try {
      setLoading(true);
      const response = await chemicalInventoryApi.createFromProduct(product.id);
      if (response.data) {
        setChemicalInventory(response.data);
        toast.success(t("chemical.inventory.added_from_product"));
      } else {
        toast.error(t("chemical.inventory.add_from_product_error"));
      }
    } catch (err) {
      console.error("Error adding to chemical inventory:", err);
      toast.error(t("chemical.inventory.add_from_product_error"));
    } finally {
      setLoading(false);
    }
  };

  const hasChemicalInfo =
    product?.cas_number ||
    product?.chemical_formula ||
    product?.molecular_weight ||
    product?.purity ||
    product?.physical_state ||
    product?.color ||
    product?.odor;

  if (!hasChemicalInfo && !chemicalInventory) {
    return null;
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <BeakerIcon className="h-5 w-5 text-blue-500 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">
              {t("chemical.inventory.basic_info")}
            </h3>
          </div>
          {!chemicalInventory && autoCreateEnabled ? (
            <RoleOnly roles={CHEMICAL_INVENTORY_WRITE_ROLES}>
              <button
                onClick={handleAddToChemicalInventory}
                disabled={loading}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                {loading
                  ? t("chemical.inventory.adding")
                  : t("chemical.inventory.add_from_product")}
              </button>
            </RoleOnly>
          ) : null}
        </div>

        {chemicalInventory && (
          <div className="mb-4 p-3 bg-blue-50 rounded-md">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <EyeIcon className="h-4 w-4 text-blue-500 mr-2" />
                <span className="text-sm font-medium text-blue-900">
                  {t("chemical.inventory.created_for_product")}
                </span>
              </div>
              <Link
                to={`/dashboard/hse-ks/risk/chemical-inventory/${chemicalInventory.id}`}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {t("chemical.inventory.view_details")}
              </Link>
            </div>
          </div>
        )}

        {hasChemicalInfo && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {product?.cas_number && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t("chemical.inventory.cas_number")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {product.cas_number}
                </dd>
              </div>
            )}

            {product?.chemical_formula && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t("chemical.inventory.chemical_formula")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {product.chemical_formula}
                </dd>
              </div>
            )}

            {product?.molecular_weight && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t("chemical.inventory.molecular_weight")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {product.molecular_weight}
                </dd>
              </div>
            )}

            {product?.purity && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t("chemical.inventory.purity")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">{product.purity}</dd>
              </div>
            )}

            {product?.physical_state && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t("chemical.inventory.physical_state")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {product.physical_state}
                </dd>
              </div>
            )}

            {product?.color && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t("chemical.inventory.color")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">{product.color}</dd>
              </div>
            )}

            {product?.odor && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t("chemical.inventory.odor")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">{product.odor}</dd>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
